/**
 * FleetTracko Admin Services
 */

import { api } from '../lib/api';
import type {
  Vehicle,
  Task,
  ServiceInstance,
  ServiceFilters,
  Customer,
  YardZone,
  YardPosition,
  WorkflowDefinition,
  WorkflowInstance,
  PaginationParams,
  VehicleFilters,
  TaskFilters,
  UserFilters,
  DashboardStats,
  ZoneOccupancy,
  UrgentItem,
  Invoice,
  InvoiceFilters,
  BillingSummary,
  KVA,
  KVAFilters,
  DamageCase,
  DamageCaseFilters,
  OpenRepairIssue,
  RepairOrder,
  RepairOrderFilters,
  InsuranceCase,
  InsuranceCaseFilters,
} from '../types';

// Type for paginated API responses
type PaginatedResponse<T> = {
  items: T[];
  meta: { total: number; page: number; limit: number; totalPages: number };
};

// Dashboard Services
export const dashboardService = {
  getStats: (params?: { locationId?: string }) =>
    api.get<DashboardStats>('/dashboard/stats', { params }),
  // Echte Durchlaufzeiten & Tages-Durchsatz (arrivedAt/departedAt-basiert)
  getThroughput: (params?: { locationId?: string }) => api.get('/dashboard/throughput', { params }),
  getActivity: () => api.get('/dashboard/activity'),
  // Zone occupancy comes from yard service
  getZoneOccupancy: () => api.get<ZoneOccupancy[]>('/yard/occupancy'),
  // Urgent items from tasks with high priority
  getUrgentItems: () =>
    api.get<UrgentItem[]>('/tasks', { params: { priority: 'URGENT', status: 'PENDING' } }),
};

// Vehicle Services
export interface VehicleStay {
  id: string;
  stayNumber: string;
  sequenceNo: number;
  status: 'OPEN' | 'CLOSED';
  arrivedAt: string | null;
  departedAt: string | null;
  totalLossAt: string | null;
  isCurrent: boolean;
  summary: {
    services: number;
    damages: number;
    checkIns: number;
    billingLines: number;
    billingNet: number;
  };
}

export const vehicleService = {
  getAll: (params: PaginationParams & VehicleFilters) =>
    api.get<PaginatedResponse<Vehicle>>('/vehicles', {
      params: { ...params, page: (params.page || 1) - 1 },
    }),
  getById: (id: string) => api.get<Vehicle>(`/vehicles/${id}`),
  // Vorgänge (Aufenthalte) des Fahrzeugs, neueste zuerst, mit Zusammenfassung
  stays: (id: string) => api.get<VehicleStay[]>(`/vehicles/${id}/stays`),
  getByVin: (vin: string) => api.get<Vehicle>(`/vehicles/vin/${encodeURIComponent(vin)}`),
  // Fuzzy match on VIN OR Kennzeichen (contains, case-insensitive)
  search: (q: string) => api.get<Vehicle[]>('/vehicles/search', { params: { q } }),
  create: (data: Partial<Vehicle>) => api.post<Vehicle>('/vehicles', data),
  update: (id: string, data: Partial<Vehicle>) => api.patch<Vehicle>(`/vehicles/${id}`, data),
  delete: (id: string) => api.delete(`/vehicles/${id}`),
  // Use correct endpoint for position assignment
  move: (id: string, positionId: string) =>
    api.post(`/vehicles/${id}/assign-position`, { positionId }),
  // Stellplatz freigeben ohne neues Ziel (transaktional + VehicleHistory im Backend)
  releasePosition: (id: string) => api.post(`/vehicles/${id}/release-position`),
  transferLocation: (id: string, locationId: string | null) =>
    api.post<Vehicle>(`/vehicles/${id}/transfer`, { locationId }),
  intake: (data: {
    vin: string;
    licensePlate?: string;
    internalNumber?: string;
    locationId?: string;
    make: string;
    model: string;
    year?: number;
    color?: string;
    fuelType?: string;
    customerId?: string;
    contractId?: string;
    mileage?: number;
    fuelLevel?: number;
    stateOfCharge?: number;
    keyCount?: number;
    positionId?: string;
    announcedArrival?: string;
    metadata?: Record<string, unknown>;
  }) => api.post<Vehicle>('/vehicles/intake', data),
  checkout: (id: string, data: { mileage?: number; notes?: string; force?: boolean }) =>
    api.post<Vehicle>(`/vehicles/${id}/checkout`, data),
  // Totalschaden-Stichtag setzen/entfernen (null = entfernen). Ab dem Datum
  // bucht die Standtage-Engine STANDTAG-TS statt STANDTAG.
  setTotalLoss: (id: string, totalLossAt: string | null) =>
    api.post<{ id: string; vin: string; totalLossAt: string | null }>(
      `/vehicles/${id}/total-loss`,
      { totalLossAt },
    ),
  reassignContract: (id: string, contractId: string) =>
    api.post<Vehicle>(`/vehicles/${id}/reassign-contract`, { contractId }),
  // Manually start (or restart with force) the vehicle's process/workflow.
  // Omit definitionId to let the backend resolve it (contract → tenant default).
  startWorkflow: (id: string, data?: { definitionId?: string; force?: boolean }) =>
    api.post<Vehicle>(`/vehicles/${id}/start-workflow`, data || {}),
  // Vorlauf (pre-arrival) bulk import
  importVorlaufExcel: (file: File, defaultCustomerId?: string) => {
    const fd = new FormData();
    fd.append('file', file);
    const qs = defaultCustomerId
      ? `?defaultCustomerId=${encodeURIComponent(defaultCustomerId)}`
      : '';
    return api.uploadFile<{
      total: number;
      created: number;
      updated: number;
      rejected: Array<{ row: number; vin: string | null; reason: string }>;
    }>(`/vehicles/vorlauf/import-excel${qs}`, fd);
  },
  downloadVorlaufTemplate: async () => {
    const blob = (await api.get('/vehicles/vorlauf/template', {
      responseType: 'blob',
    })) as unknown as Blob;
    return blob;
  },
  getHistory: (id: string) => api.get(`/vehicles/${id}/history`),
  // Mandantenweiter Fahrzeugmodell-Katalog (Marke/Modell normalisiert).
  modelCatalog: () => api.get<VehicleModelEntry[]>('/vehicles/model-catalog'),
  replaceModelCatalog: (entries: VehicleModelEntry[]) =>
    api.put<VehicleModelEntry[]>('/vehicles/model-catalog', { entries }),
  renameModelCatalog: (dto: {
    kind: 'make' | 'model';
    make?: string;
    from: string;
    to: string;
    propagate?: boolean;
  }) => api.put<unknown>('/vehicles/model-catalog/rename', dto),
  // Gesamtbestand-Export (unpaginiert, serverseitig) mit den Listen-Filtern
  exportAll: async (params: {
    status?: string;
    search?: string;
    locationId?: string;
    hasPosition?: string;
  }) => {
    const blob = (await api.get('/reports/vehicles/export', {
      params: { format: 'xlsx', ...params },
      responseType: 'blob',
    })) as unknown as Blob;
    return blob;
  },
  getStats: () =>
    api.get<{ total: number; byStatus: Record<string, number> }>('/dashboard/vehicles'),
};

// Check-In Services (the CheckIn entity — photos/checklist/condition-writeback +
// ANNOUNCED/INCOMING → ON_YARD promotion). Same endpoints the mobile app uses.
export interface VehicleModelEntry {
  make: string;
  model: string;
  segment?: string | null;
  drive?: string | null;
}

export interface CheckInItemPayload {
  itemKey: string;
  label: string;
  isChecked: boolean;
  value?: string;
  notes?: string;
}

export const checkinService = {
  // Historie: alle Check-ins eines Fahrzeugs (neueste zuerst).
  getAll: (params?: { vehicleId?: string; page?: number; limit?: number }) =>
    api.get<{ items?: CheckIn[] } | CheckIn[]>('/checkins', { params }),
  // Voller Datensatz inkl. Checkliste (items) und Foto-Metadaten.
  getById: (id: string) => api.get<CheckIn>(`/checkins/${id}`),
  // Foto-Binary durch die API (gespeicherte presigned MinIO-URLs sind im Browser tot).
  getPhotoBlob: async (checkInId: string, photoId: string, thumbnail = false) => {
    const blob = (await api.get(`/checkins/${checkInId}/photos/${photoId}/file`, {
      params: thumbnail ? { thumbnail: 'true' } : undefined,
      responseType: 'blob',
    })) as unknown as Blob;
    return blob;
  },

  // Start a check-in for an existing vehicle. Note: inline `items` on this call are
  // NOT persisted by the API — send the checklist via updateItems() after start.
  start: (data: {
    vehicleId: string;
    checkInType?: 'CLASSIC' | 'LIGHT' | 'PREP_CHECK';
    /** Tatsächlicher Fahrzeugeingang (ISO) — bestimmt Vorgangsstart/Standtage. */
    arrivedAt?: string;
    mileage?: number;
    fuelLevel?: number;
    stateOfCharge?: number;
    keyCount?: number;
    items?: CheckInItemPayload[];
    notes?: string;
    metadata?: Record<string, unknown>;
  }) => api.post<CheckIn>('/checkins', data),
  // Writes fuel/SoC/mileage back to the vehicle and promotes ANNOUNCED/INCOMING → ON_YARD.
  complete: (id: string, data?: Record<string, unknown>) =>
    api.post<CheckIn>(`/checkins/${id}/complete`, data ?? {}),
  // Upsert the checklist items (the reliable persistence path for Step-4 data).
  updateItems: (id: string, items: CheckInItemPayload[]) =>
    api.post<{ message: string }>(`/checkins/${id}/items`, items),
  // Desktop counterpart to the mobile camera capture: upload a local file, then
  // register the CheckInPhoto record (same two-step + records as the mobile flow).
  uploadPhoto: async (checkInId: string, file: File, position: string) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('position', position);
    const uploadResult = await api.uploadFile<{ url: string; key: string; filename: string }>(
      `/storage/upload/checkin/${checkInId}`,
      formData,
    );
    return api.post(`/checkins/${checkInId}/photos`, {
      position,
      filePath: uploadResult.data?.url || uploadResult.data?.key,
      storageKey: uploadResult.data?.key,
    });
  },
};

export interface CheckIn {
  id: string;
  vehicleId: string;
  stayId?: string | null;
  checkInType: string;
  mileage?: number | null;
  fuelLevel?: number | null;
  stateOfCharge?: number | null;
  keyCount?: number | null;
  startedAt?: string;
  completedAt?: string | null;
  remarks?: string | null;
  performedBy?: { id: string; firstName?: string; lastName?: string } | null;
  items?: Array<{
    id: string;
    itemKey: string;
    label?: string | null;
    isChecked: boolean;
    value?: string | null;
  }>;
  photos?: Array<{ id: string; position: string; mimeType?: string | null }>;
  vehicle?: { id: string; status?: string; [key: string]: unknown };
}

// Task Services
export const taskService = {
  getAll: (params: PaginationParams & TaskFilters) =>
    api.get<PaginatedResponse<Task>>('/tasks', {
      params: { ...params, page: (params.page || 1) - 1 },
    }),
  getById: (id: string) => api.get<Task>(`/tasks/${id}`),
  getMyTasks: () => api.get<Task[]>('/tasks/my'),
  create: (data: {
    vehicleId?: string;
    taskType: string;
    title: string;
    description?: string;
    priority?: 'LOW' | 'NORMAL' | 'HIGH' | 'URGENT';
    dueDate?: string;
    assigneeId?: string;
    notes?: string;
    metadata?: Record<string, unknown>;
  }) => api.post<Task>('/tasks', data),
  update: (
    id: string,
    data: {
      title?: string;
      description?: string;
      priority?: 'LOW' | 'NORMAL' | 'HIGH' | 'URGENT';
      dueDate?: string;
      assigneeId?: string;
      notes?: string;
    },
  ) => api.patch<Task>(`/tasks/${id}`, data),
  assign: (id: string, assigneeId: string) => api.post(`/tasks/${id}/assign`, { assigneeId }),
  start: (id: string) => api.post(`/tasks/${id}/start`),
  complete: (id: string, data?: Record<string, unknown>) => api.post(`/tasks/${id}/complete`, data),
  cancel: (id: string, reason?: string) => api.post(`/tasks/${id}/cancel`, { reason }),
};

// Customer Services
export const customerService = {
  getAll: (params?: PaginationParams) =>
    api.get<PaginatedResponse<Customer>>('/customers', {
      params: params ? { ...params, page: (params.page || 1) - 1 } : undefined,
    }),
  getById: (id: string) => api.get<Customer>(`/customers/${id}`),
  create: (data: Partial<Customer>) => api.post<Customer>('/customers', data),
  update: (id: string, data: Partial<Customer>) => api.patch<Customer>(`/customers/${id}`, data),
};

// Contract Types
export interface Contract {
  id: string;
  name: string;
  contractNumber: string;
  customerId: string;
  customer?: { id: string; code: string; name: string };
  workflowDefinitionId?: string;
  workflowDefinition?: { id: string; name: string; key: string };
  priceListId?: string;
  priceList?: { id: string; name: string; version: string };
  validFrom?: string;
  validTo?: string;
  isActive: boolean;
  vehicleCount?: number;
  slaConfig?: Record<string, unknown>;
  serviceScope?: Record<string, unknown>;
}

// Contract Services
export const contractService = {
  getAll: (params?: { customerId?: string }) =>
    api.get<{ items: Contract[]; total: number }>('/contracts', { params }),
  getByCustomer: (customerId: string) =>
    api.get<{ items: Contract[]; total: number }>(`/contracts/customer/${customerId}`),
  getById: (id: string) => api.get<Contract>(`/contracts/${id}`),
  create: (data: {
    name: string;
    contractNumber: string;
    customerId: string;
    workflowDefinitionId?: string;
    priceListId?: string;
    validFrom?: string;
    validTo?: string;
    slaConfig?: Record<string, unknown>;
    serviceScope?: Record<string, unknown>;
  }) => api.post<Contract>('/contracts', data),
  update: (
    id: string,
    data: {
      name?: string;
      workflowDefinitionId?: string | null;
      priceListId?: string | null;
      validFrom?: string;
      validTo?: string;
      isActive?: boolean;
      slaConfig?: Record<string, unknown>;
      serviceScope?: Record<string, unknown>;
    },
  ) => api.put<Contract>(`/contracts/${id}`, data),
  delete: (id: string) => api.delete(`/contracts/${id}`),
};

// Yard Services
export const yardService = {
  getOverview: () => api.get('/yard/overview'),
  getOccupancy: () => api.get('/yard/occupancy'),
  getCapacity: () => api.get('/yard/capacity'),
  // Get zones from capacity endpoint (zones are nested in capacity response)
  getZones: async () => {
    const response = await api.get('/yard/capacity');
    // API returns { zones: [...], totals: {...} } after api.get unwraps response.data
    type ZoneData = {
      id: string;
      name: string;
      code: string;
      zoneType: string;
      locationId: string;
      locationName: string;
      totalPositions: number;
      occupiedPositions: number;
      availablePositions: number;
    };
    type CapacityResponse = {
      zones: ZoneData[];
      totals: { total: number; occupied: number; available: number };
    };

    const capacityData = response.data as CapacityResponse;
    const zones = capacityData?.zones || [];

    // Map zoneType to valid YardZoneType for YardMap component
    type ZoneType = 'parking' | 'cleaning' | 'damage' | 'staging';
    const zoneTypeMap: Record<string, ZoneType> = {
      parking: 'parking',
      cleaning: 'cleaning',
      damage: 'damage',
      staging: 'staging',
      entry: 'staging',
      checkin: 'staging',
      workshop: 'damage',
      ev: 'parking',
      pool: 'parking',
      longterm: 'parking',
    };

    const mappedZones = zones.map((zone) => {
      const zoneTypeLower = zone.zoneType?.toLowerCase() || 'parking';
      const type: ZoneType = zoneTypeMap[zoneTypeLower] || 'parking';
      const occupancyPercent =
        zone.totalPositions > 0
          ? Math.round((zone.occupiedPositions / zone.totalPositions) * 100)
          : 0;
      return {
        id: zone.id,
        name: zone.name,
        code: zone.code,
        capacity: zone.totalPositions,
        type,
        zoneType: zone.zoneType,
        occupied: zone.occupiedPositions,
        available: zone.availablePositions,
        occupancyPercent,
        yardId: zone.locationId,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };
    });
    return { data: mappedZones };
  },
  // Get positions for all zones (fetches from each zone)
  getPositions: async (params?: { zoneId?: string; locationId?: string }) => {
    // Helper to transform API position to YardPosition
    type ApiPosition = {
      id: string;
      code: string;
      zoneId: string;
      isOccupied: boolean;
      isActive: boolean;
      vehicles: Array<{
        id: string;
        vin: string;
        licensePlate?: string;
        make?: string;
        model?: string;
        color?: string;
        status: string;
      }>;
    };

    const transformPosition = (pos: ApiPosition, index: number): YardPosition => {
      // Parse row/column from position code (e.g., "ENTRY-001" → row based on batch, column from number)
      const match = pos.code.match(/(\d+)$/);
      const posNum = match ? parseInt(match[1], 10) : index + 1;
      // Arrange in grid: 5 columns per row
      const row = Math.floor((posNum - 1) / 5);
      const column = (posNum - 1) % 5;

      const vehicle = pos.vehicles?.[0];
      return {
        id: pos.id,
        zoneId: pos.zoneId,
        code: pos.code,
        row,
        column,
        vehicleId: vehicle?.id,
        vehicle: vehicle
          ? {
              id: vehicle.id,
              vin: vehicle.vin,
              licensePlate: vehicle.licensePlate,
              make: vehicle.make,
              model: vehicle.model,
              color: vehicle.color,
              status: vehicle.status,
            }
          : undefined,
        isBlocked: !pos.isActive,
        isOccupied: pos.isOccupied,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };
    };

    // Fetch positions for a specific zone
    if (params?.zoneId) {
      const response = await api.get(`/zones/${params.zoneId}/positions`, {
        params: { includeVehicle: true, limit: 500 },
      });
      // API returns { zone: {...}, positions: [...], stats: {...} }
      type ZonePositionsResponse = { zone: unknown; positions: ApiPosition[]; stats: unknown };
      const data = response.data as ZonePositionsResponse;
      const positions = (data?.positions || []).map((pos, idx) => transformPosition(pos, idx));
      return { data: positions };
    }

    // For yard map, get ALL positions from ALL zones
    const capacityResponse = await api.get('/yard/capacity');
    type CapacityResponse = { zones: Array<{ id: string; code: string }>; totals: unknown };
    const capacityData = capacityResponse.data as CapacityResponse;
    const zones = capacityData?.zones || [];

    const allPositions: YardPosition[] = [];

    // Fetch positions for each zone
    for (const zone of zones) {
      try {
        const response = await api.get(`/zones/${zone.id}/positions`, {
          params: { includeVehicle: true, limit: 500 },
        });
        type ZonePositionsResponse = { zone: unknown; positions: ApiPosition[]; stats: unknown };
        const data = response.data as ZonePositionsResponse;
        const positions = (data?.positions || []).map((pos, idx) => transformPosition(pos, idx));
        allPositions.push(...positions);
      } catch {
        // Zone might not have positions endpoint, continue
      }
    }

    return { data: allPositions };
  },
  getMap: (locationId: string) => api.get(`/yard/map/${locationId}`),
  // Use vehicle endpoint for position assignment
  moveVehicle: (vehicleId: string, targetPositionId: string) =>
    api.post(`/vehicles/${vehicleId}/assign-position`, { positionId: targetPositionId }),
  searchVehicles: (query: string) => api.get('/yard/search', { params: { query } }),
  // Get all positions for a zone (including empty ones)
  getZonePositions: async (zoneId: string) => {
    const response = await api.get(`/zones/${zoneId}/positions`, {
      params: { includeVehicle: true, limit: 500 },
    });
    // API returns { zone: {...}, positions: [...], stats: {...} }
    type ApiPosition = {
      id: string;
      code: string;
      zoneId: string;
      isOccupied: boolean;
      isActive: boolean;
      vehicles: Array<{
        id: string;
        vin: string;
        licensePlate?: string;
        make?: string;
        model?: string;
        color?: string;
        status: string;
      }>;
    };
    type ZonePositionsResponse = { zone: unknown; positions: ApiPosition[]; stats: unknown };
    const data = response.data as ZonePositionsResponse;
    const positions = (data?.positions || []).map((pos, idx) => {
      const match = pos.code.match(/(\d+)$/);
      const posNum = match ? parseInt(match[1], 10) : idx + 1;
      const row = Math.floor((posNum - 1) / 5);
      const column = (posNum - 1) % 5;
      const vehicle = pos.vehicles?.[0];
      return {
        id: pos.id,
        zoneId: pos.zoneId,
        code: pos.code,
        row,
        column,
        vehicleId: vehicle?.id,
        vehicle: vehicle
          ? {
              id: vehicle.id,
              vin: vehicle.vin,
              licensePlate: vehicle.licensePlate,
              make: vehicle.make,
              model: vehicle.model,
              color: vehicle.color,
              status: vehicle.status,
            }
          : undefined,
        isBlocked: !pos.isActive,
        isOccupied: pos.isOccupied,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };
    });
    return { data: positions };
  },
  // Get all available (unoccupied) positions across all zones
  getAvailablePositions: async () => {
    // First get all zones from capacity endpoint
    const capacityResponse = await api.get('/yard/capacity');
    type ZoneData = { id: string; name: string; code: string; locationName: string };
    type CapacityResponse = { zones: ZoneData[]; totals: unknown };
    const capacityData = capacityResponse.data as CapacityResponse;
    const zones = capacityData?.zones || [];

    type ApiPosition = {
      id: string;
      code: string;
      zoneId: string;
      isOccupied: boolean;
      isActive: boolean;
      vehicles: Array<{
        id: string;
        vin: string;
        licensePlate?: string;
        make?: string;
        model?: string;
        color?: string;
        status: string;
      }>;
    };

    const allPositions: Array<YardPosition & { zoneName?: string; locationName?: string }> = [];

    // Get positions for each zone
    for (const zone of zones) {
      try {
        const response = await api.get(`/zones/${zone.id}/positions`, {
          params: { includeVehicle: true, limit: 500 },
        });
        type ZonePositionsResponse = { zone: unknown; positions: ApiPosition[]; stats: unknown };
        const data = response.data as ZonePositionsResponse;

        for (const [idx, pos] of (data?.positions || []).entries()) {
          const match = pos.code.match(/(\d+)$/);
          const posNum = match ? parseInt(match[1], 10) : idx + 1;
          const row = Math.floor((posNum - 1) / 5);
          const column = (posNum - 1) % 5;
          const vehicle = pos.vehicles?.[0];

          allPositions.push({
            id: pos.id,
            zoneId: pos.zoneId,
            code: pos.code,
            row,
            column,
            vehicleId: vehicle?.id,
            vehicle: vehicle
              ? {
                  id: vehicle.id,
                  vin: vehicle.vin,
                  licensePlate: vehicle.licensePlate,
                  make: vehicle.make,
                  model: vehicle.model,
                  color: vehicle.color,
                  status: vehicle.status,
                }
              : undefined,
            isBlocked: !pos.isActive,
            isOccupied: pos.isOccupied,
            zoneName: zone.name,
            zoneCode: zone.code,
            locationName: zone.locationName,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
          });
        }
      } catch {
        // Zone might not have positions endpoint, continue
      }
    }

    return { data: allPositions };
  },
};

// Workflow Services
export const workflowService = {
  getDefinitions: () => api.get<WorkflowDefinition[]>('/workflow-definitions'),
  getDefinition: (id: string) => api.get<WorkflowDefinition>(`/workflow-definitions/${id}`),
  // Vorgeschlagene Default-Definition für ein Fahrzeug (Vertrag → Tenant-Default).
  getDefaultDefinition: (vehicleId: string) =>
    api.get<{ definitionId: string | null }>('/workflow-instances/default-definition', {
      params: { vehicleId },
    }),
  create: (data: {
    key: string;
    name: string;
    description?: string;
    definition: Record<string, unknown>;
    triggerConfig?: Record<string, unknown>;
    isActive?: boolean;
    isDefault?: boolean;
  }) => api.post<WorkflowDefinition>('/workflow-definitions', data),
  update: (
    id: string,
    data: {
      name?: string;
      description?: string;
      definition?: Record<string, unknown>;
      triggerConfig?: Record<string, unknown>;
      isActive?: boolean;
      isDefault?: boolean;
    },
  ) => api.patch<WorkflowDefinition>(`/workflow-definitions/${id}`, data),
  delete: (id: string) => api.delete(`/workflow-definitions/${id}`),
  getInstances: (params?: PaginationParams & { workflowId?: string }) =>
    api.get<PaginatedResponse<WorkflowInstance>>('/workflow-instances', {
      params: params ? { ...params, page: (params.page || 1) - 1 } : undefined,
    }),
  getInstance: (id: string) => api.get<WorkflowInstance>(`/workflow-instances/${id}`),
  advanceInstance: (id: string, trigger: string, data?: Record<string, unknown>) =>
    api.post<WorkflowInstance>(`/workflow-instances/${id}/advance`, { trigger, data }),
  completeStep: (id: string, stepId: string, data?: Record<string, unknown>) =>
    api.post<WorkflowInstance>(`/workflow-instances/${id}/advance`, {
      trigger: `${stepId}_COMPLETED`,
      data,
    }),
  getStats: () => api.get('/workflows/stats'),
  getDashboard: () => api.get('/workflows/dashboard'),
};

// User Services
export const userService = {
  getAll: (params?: PaginationParams & UserFilters) =>
    api.get('/users', { params: params ? { ...params, page: (params.page || 1) - 1 } : undefined }),
  getById: (id: string) => api.get(`/users/${id}`),
  create: (data: unknown) => api.post('/users', data),
  update: (id: string, data: unknown) => api.patch(`/users/${id}`, data),
  activate: (id: string) => api.post(`/users/${id}/activate`),
  deactivate: (id: string) => api.post(`/users/${id}/deactivate`),
  resetPassword: (id: string) => api.post(`/users/${id}/reset-password`),
};

// Task type display names (German)
const TASK_TYPE_LABELS: Record<string, string> = {
  CHECKIN: 'Check-In',
  CHECKOUT: 'Check-Out',
  DAMAGE_ASSESSMENT: 'Schadenaufnahme',
  CLEANING: 'Reinigung',
  QC: 'QS-Prüfung',
  MOVE: 'Umparken',
  HANDOVER: 'Übergabe',
  REPAIR: 'Reparatur',
  OTHER: 'Sonstiges',
};

// Invoice Services
export const invoiceService = {
  getAll: (params?: PaginationParams & InvoiceFilters) =>
    api.get<{
      items: Invoice[];
      meta: { total: number; page: number; limit: number; totalPages: number };
    }>('/invoices', { params: params ? { ...params, page: (params.page || 1) - 1 } : undefined }),
  getById: (id: string) => api.get<Invoice>(`/invoices/${id}`),
  create: (data: {
    customerId: string;
    invoiceDate?: string;
    dueDate?: string;
    notes?: string;
    internalNotes?: string;
    items?: Array<{
      serviceCode: string;
      description: string;
      quantity: number;
      unit?: string;
      unitPrice: number;
      taxRate?: number;
      notes?: string;
    }>;
  }) => api.post<Invoice>('/invoices', data),
  update: (id: string, data: Partial<Invoice>) => api.patch<Invoice>(`/invoices/${id}`, data),
  delete: (id: string) => api.delete(`/invoices/${id}`),
  // Items
  addItem: (
    invoiceId: string,
    item: {
      serviceCode: string;
      description: string;
      quantity: number;
      unitPrice: number;
      taxRate?: number;
      notes?: string;
    },
  ) => api.post<Invoice>(`/invoices/${invoiceId}/items`, item),
  updateItem: (
    invoiceId: string,
    itemId: string,
    data: Partial<{
      description: string;
      quantity: number;
      unitPrice: number;
      taxRate: number;
      notes: string;
    }>,
  ) => api.patch<Invoice>(`/invoices/${invoiceId}/items/${itemId}`, data),
  removeItem: (invoiceId: string, itemId: string) =>
    api.delete(`/invoices/${invoiceId}/items/${itemId}`),
  // Workflow actions
  submit: (id: string) => api.post<Invoice>(`/invoices/${id}/submit`),
  approve: (id: string) => api.post<Invoice>(`/invoices/${id}/approve`),
  send: (id: string) => api.post<Invoice>(`/invoices/${id}/send`),
  markPaid: (
    id: string,
    data?: { amount?: number; paymentDate?: string; paymentReference?: string },
  ) => api.post<Invoice>(`/invoices/${id}/mark-paid`, data),
  cancel: (id: string, reason?: string) => api.post<Invoice>(`/invoices/${id}/cancel`, { reason }),
  // PDF
  downloadPdf: (id: string) =>
    api.get(`/invoices/${id}/pdf/download`, { responseType: 'blob' } as Record<string, unknown>),
  // Billing
  getBillingSummary: (params?: { customerId?: string; dateFrom?: string; dateTo?: string }) =>
    api.get<BillingSummary>('/billing/summary', { params }),
  getOutstanding: () => api.get('/billing/outstanding'),
  getCustomerBilling: (customerId: string) => api.get(`/billing/customer/${customerId}`),
  // Billable positions resolved from the customer's contract price list
  getPriceItems: (customerId: string) =>
    api.get<CustomerPriceItemsResponse>('/invoices/price-items', { params: { customerId } }),
  // Manual trigger for the daily OVERDUE sweep (admins)
  processOverdue: () =>
    api.post<{ processed: number; marked: number }>('/invoices/process-overdue'),
  // Unbilled service records grouped by customer
  getBillable: (params?: { customerId?: string; startDate?: string; endDate?: string }) =>
    api.get<BillableGroup[]>('/billing/billable', { params }),
  generateFromBillable: (data: { customerId: string }) =>
    api.post<Invoice>('/billing/generate-invoice', data),
};

// Invoice billing helper types
export interface CustomerPriceItem {
  serviceCode: string;
  name: string;
  description: string;
  unit: string;
  price: number;
  taxRate: number;
}

export interface CustomerPriceItemsResponse {
  customer: { id: string; name: string; code: string };
  source: 'contract' | 'customer' | 'default' | null;
  priceList: {
    id: string;
    name: string;
    currency: string;
    discountPercent: number;
    contractId: string | null;
    contractNumber: string | null;
  } | null;
  items: CustomerPriceItem[];
}

export interface BillableServiceRecord {
  id: string;
  serviceCode: string;
  description: string;
  quantity: string | number;
  unit: string;
  unitPriceNet: string | number;
  totalNet: string | number;
  vehicleId?: string | null;
  customerId?: string | null;
  meterDate?: string | null;
  createdAt: string;
  vehicle?: {
    id: string;
    vin: string;
    licensePlate?: string | null;
    internalNumber?: string | null;
  } | null;
}

export interface BillableGroup {
  customerId: string;
  customer?: { id: string; name: string; code: string } | null;
  records: BillableServiceRecord[];
  totalAmount: number;
}

// KVA Services
export const kvaService = {
  getAll: (params?: PaginationParams & KVAFilters) =>
    api.get<{
      items: KVA[];
      meta: { total: number; page: number; limit: number; totalPages: number };
    }>('/kva', { params: params ? { ...params, page: (params.page || 1) - 1 } : undefined }),
  getById: (id: string) => api.get<KVA>(`/kva/${id}`),
  create: (data: {
    damageCaseId: string;
    items?: Array<{
      description: string;
      category: string;
      quantity: number;
      unitPrice: number;
      notes?: string;
    }>;
    notes?: string;
    expiresAt?: string;
  }) => {
    // Map expiresAt to validUntil and exclude expiresAt from request
    const { expiresAt, ...rest } = data;
    return api.post<KVA>('/kva', {
      ...rest,
      validUntil: expiresAt,
    });
  },
  update: (id: string, data: Partial<KVA>) => api.patch<KVA>(`/kva/${id}`, data),
  // Workflow
  submit: (id: string) => api.post<KVA>(`/kva/${id}/submit`),
  // Freigeben/Ablehnen selbst ist dem Kunden vorbehalten (CUSTOMER_ADMIN im
  // Portal). Intern wird die Entscheidung erfasst, die telefonisch, per Mail
  // oder persönlich kam — inkl. Name und Kanal für die Freigabe-Historie.
  recordApproval: (
    id: string,
    data: {
      decision: 'APPROVED' | 'REJECTED';
      approvedBy: string;
      channel: 'PHONE' | 'EMAIL' | 'IN_PERSON';
      note?: string;
      approvedItemIds?: string[];
    },
  ) => api.post<KVA>(`/kva/${id}/record-approval`, data),
  // Items
  addItem: (
    kvaId: string,
    item: {
      description: string;
      category: string;
      quantity: number;
      /** LABOR | PART | MATERIAL | OTHER — Art der Leistung. */
      itemType?: string;
      /** PIECE | HOUR | DAY | LITER | FLAT — Vokabular aus @fleettracko/shared. */
      unit?: string;
      unitPrice: number;
      notes?: string;
    },
  ) => api.post<KVA>(`/kva/${kvaId}/items`, item),
  removeItem: (kvaId: string, itemId: string) => api.delete(`/kva/${kvaId}/items/${itemId}`),
};

// Damage Case Services
export const damageCaseService = {
  getAll: (params?: PaginationParams & DamageCaseFilters) =>
    api.get<{
      items: DamageCase[];
      meta: { total: number; page: number; limit: number; totalPages: number };
    }>('/damage-cases', {
      params: params ? { ...params, page: (params.page || 1) - 1 } : undefined,
    }),
  getById: (id: string) => api.get<DamageCase>(`/damage-cases/${id}`),
  create: (data: {
    vehicleId: string;
    customerId?: string;
    isPreExisting?: boolean;
    items?: Array<{
      position: string;
      damageType: string;
      severity: string;
      description?: string;
      estimatedCost?: number;
    }>;
    notes?: string;
  }) => api.post<DamageCase>('/damage-cases', data),
  update: (id: string, data: Partial<DamageCase>) =>
    api.patch<DamageCase>(`/damage-cases/${id}`, data),
  submit: (id: string) => api.post<DamageCase>(`/damage-cases/${id}/submit`),
  createKVA: (id: string) => api.post<KVA>(`/damage-cases/${id}/create-kva`),
  // Items
  addItem: (
    caseId: string,
    item: {
      position: string;
      damageType: string;
      severity: string;
      description?: string;
      estimatedCost?: number;
    },
  ) => api.post<DamageCase>(`/damage-cases/${caseId}/items`, item),
  removeItem: (caseId: string, itemId: string) =>
    api.delete(`/damage-cases/${caseId}/items/${itemId}`),
  // Photos
  getPhotos: (caseId: string) => api.get(`/damage-cases/${caseId}/photos`),
  uploadPhoto: async (caseId: string, itemId: string, file: File) => {
    // Step 1: Upload file to storage
    const formData = new FormData();
    formData.append('file', file);
    formData.append('itemId', itemId);
    const uploadResult = await api.uploadFile<{ url: string; key: string; filename: string }>(
      `/storage/upload/damage/${caseId}`,
      formData,
    );

    // Step 2: Create photo record in database
    const photoData = {
      filePath: uploadResult.data?.url || uploadResult.data?.key,
      storageKey: uploadResult.data?.key,
      photoType: 'detail',
    };
    return api.post(`/damage-cases/${caseId}/items/${itemId}/photos`, photoData);
  },
};

// Repair Order Services
export const repairOrderService = {
  getAll: (params?: PaginationParams & RepairOrderFilters) =>
    api.get<{
      items: RepairOrder[];
      meta: { total: number; page: number; limit: number; totalPages: number };
    }>('/repair-orders', {
      params: params ? { ...params, page: (params.page || 1) - 1 } : undefined,
    }),
  getById: (id: string) => api.get<RepairOrder>(`/repair-orders/${id}`),
  // Sammelstelle für offene Werkstatt-Meldungen (tenant-weit), damit nichts
  // nur im Auftragsdetail hängt.
  getOpenIssues: (params?: { limit?: number }) =>
    api.get<{
      items: OpenRepairIssue[];
      meta: { total: number; limit: number; truncated: boolean };
    }>('/repair-orders/issues/open', { params }),
  // PDF-Anhänge (Kalkulationen/Gutachten) — Auslieferung über presigned URLs
  listDocuments: (id: string) =>
    api.get<Array<{ id: string; name: string; size: number; uploadedAt: string; url: string }>>(
      `/repair-orders/${id}/documents`,
    ),
  uploadDocument: (id: string, file: File) => {
    const form = new FormData();
    form.append('file', file);
    return api.post(`/repair-orders/${id}/documents`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  // Binary durch die API streamen — presigned URLs zeigen auf den internen
  // MinIO-Host und sind im Browser unerreichbar.
  downloadDocument: async (id: string, documentId: string) =>
    (await api.get(`/repair-orders/${id}/documents/${documentId}/download`, {
      responseType: 'blob',
    })) as unknown as Blob,
  removeDocument: (id: string, documentId: string) =>
    api.delete(`/repair-orders/${id}/documents/${documentId}`),
  create: (data: {
    vehicleId: string;
    kvaId?: string;
    title?: string;
    description?: string;
    priority?: string;
    isInternal?: boolean;
    estimatedHours?: number;
    estimatedCost?: number;
    scheduledStart?: string;
    scheduledEnd?: string;
    items?: Array<{
      itemType: string;
      category?: string;
      workType?: string;
      description: string;
      partNumber?: string;
      quantity: number;
      unitPrice: number;
    }>;
  }) => api.post<RepairOrder>('/repair-orders', data),
  update: (id: string, data: Partial<RepairOrder>) =>
    api.patch<RepairOrder>(`/repair-orders/${id}`, data),
  // Workflow
  start: (id: string) => api.post<RepairOrder>(`/repair-orders/${id}/start`),
  complete: (id: string, data?: { actualHours?: number; actualCost?: number; notes?: string }) =>
    api.post<RepairOrder>(`/repair-orders/${id}/complete`, data),
  cancel: (id: string, reason?: string) =>
    api.post<RepairOrder>(`/repair-orders/${id}/cancel`, { reason }),
  // Status update (for parts tracking)
  updateStatus: (id: string, status: string) =>
    api.patch<RepairOrder>(`/repair-orders/${id}/status`, { status }),
  // Time logging
  logTime: (
    orderId: string,
    data: {
      activity: string;
      durationMinutes: number;
      notes?: string;
    },
  ) =>
    api.post(`/repair-orders/${orderId}/time-log`, {
      ...data,
      startTime: new Date().toISOString(),
    }),
  // Items
  addItem: (
    orderId: string,
    item: {
      itemType: string;
      category?: string;
      workType?: string;
      description: string;
      partNumber?: string;
      quantity: number;
      unitPrice: number;
    },
  ) => api.post<RepairOrder>(`/repair-orders/${orderId}/items`, item),
  removeItem: (orderId: string, itemId: string) =>
    api.delete(`/repair-orders/${orderId}/items/${itemId}`),
  /** Meldung prüfen: annehmen (optional mit Position/Löschung) oder ablehnen. */
  reviewIssue: (
    orderId: string,
    issueId: string,
    data: {
      action: 'accept' | 'reject';
      comment?: string | null;
      createItem?: {
        itemType: string;
        description: string;
        quantity: number;
        unitPrice: number;
        category?: string;
        workType?: string;
      } | null;
      deleteItem?: boolean;
    },
  ) => api.patch(`/repair-orders/${orderId}/issues/${issueId}/review`, data),
  issuePhotoBlob: async (orderId: string, issueId: string) => {
    const blob = (await api.get(`/repair-orders/${orderId}/issues/${issueId}/photo`, {
      responseType: 'blob',
    })) as unknown as Blob;
    return blob;
  },
  /** Position abhaken/öffnen + Ist-Aufwand (Werkstatt-Abarbeitung). */
  updateItemStatus: (
    orderId: string,
    itemId: string,
    data: {
      status: 'pending' | 'done';
      actualMinutes?: number | null;
      actualQuantity?: number | null;
    },
  ) => api.patch(`/repair-orders/${orderId}/items/${itemId}`, data),
};

// Insurance Case Services
export const insuranceCaseService = {
  getAll: (params?: PaginationParams & InsuranceCaseFilters) =>
    api.get<{
      items: InsuranceCase[];
      meta: { total: number; page: number; limit: number; totalPages: number };
    }>('/insurance-cases', {
      params: params ? { ...params, page: (params.page || 1) - 1 } : undefined,
    }),
  getById: (id: string) => api.get<InsuranceCase>(`/insurance-cases/${id}`),
  create: (data: {
    vehicleId: string;
    damageCaseId?: string;
    caseType: string;
    insurerName?: string;
    insuranceClaimNumber?: string;
    description?: string;
    incidentDate?: string;
    policeReportNumber?: string;
    estimatedAmount?: number;
    deductible?: number;
    appraiserName?: string;
    notes?: string;
  }) => api.post<InsuranceCase>('/insurance-cases', data),
  update: (id: string, data: Partial<InsuranceCase>) =>
    api.patch<InsuranceCase>(`/insurance-cases/${id}`, data),
  // Workflow
  report: (id: string) => api.post<InsuranceCase>(`/insurance-cases/${id}/report`),
  scheduleAppraiser: (
    id: string,
    data: { scheduledAt: string; appraiserName?: string; notes?: string },
  ) => api.post<InsuranceCase>(`/insurance-cases/${id}/schedule-appraiser`, data),
  completeAppraisal: (
    id: string,
    data: {
      approvedAmount?: number;
      isTotalLoss?: boolean;
      notes?: string;
    },
  ) => api.post<InsuranceCase>(`/insurance-cases/${id}/complete-appraisal`, data),
  approve: (id: string, data?: { approvedAmount?: number; notes?: string }) =>
    api.post<InsuranceCase>(`/insurance-cases/${id}/approve`, data),
  startRepair: (id: string) => api.post<InsuranceCase>(`/insurance-cases/${id}/start-repair`),
  complete: (id: string) => api.post<InsuranceCase>(`/insurance-cases/${id}/complete`),
  markTotalLoss: (id: string, data?: { notes?: string }) =>
    api.post<InsuranceCase>(`/insurance-cases/${id}/total-loss`, data),
  close: (id: string) => api.post<InsuranceCase>(`/insurance-cases/${id}/close`),
  // Financials
  updateFinancials: (
    id: string,
    data: {
      claimedAmount?: number;
      invoicedAmount?: number;
      paidAmount?: number;
      recoveredAmount?: number;
    },
  ) => api.patch<InsuranceCase>(`/insurance-cases/${id}/financials`, data),
  // Documents
  getDocuments: (id: string) => api.get(`/insurance-cases/${id}/documents`),
  attachDocument: (
    id: string,
    data: { documentId: string; category: string; name: string; notes?: string },
  ) => api.post(`/insurance-cases/${id}/documents`, data),
  uploadAndAttachDocument: async (
    id: string,
    file: File,
    category: string,
    name: string,
    notes?: string,
  ) => {
    // Step 1: Upload file
    const formData = new FormData();
    formData.append('file', file);
    const uploadResult = await api.uploadFile<{
      id: string;
      url: string;
      key: string;
      filename: string;
    }>('/storage/upload', formData);
    const docId =
      (uploadResult.data as unknown as Record<string, unknown>)?.id ||
      (uploadResult.data as unknown as Record<string, unknown>)?.documentId;
    if (!docId) throw new Error('Upload did not return document ID');
    // Step 2: Link to insurance case
    return api.post(`/insurance-cases/${id}/documents`, {
      documentId: docId,
      category,
      name,
      notes,
    });
  },
  removeDocument: (id: string, linkId: string) =>
    api.delete(`/insurance-cases/${id}/documents/${linkId}`),
  // KVA links
  linkKva: (id: string, kvaId: string) =>
    api.post<InsuranceCase>(`/insurance-cases/${id}/link-kva`, { kvaId }),
  unlinkKva: (id: string, kvaId: string) => api.delete(`/insurance-cases/${id}/kvas/${kvaId}`),
};

// Location Types
export interface LocationData {
  id: string;
  name: string;
  code: string;
  address?: { street?: string; city?: string; postalCode?: string; country?: string } | null;
  coordinates?: { lat?: number; lng?: number } | null;
  capacity?: number;
  businessHours?: Record<string, unknown> | null;
  contactEmail?: string | null;
  contactPhone?: string | null;
  isActive: boolean;
  _count?: { zones: number };
}

export interface LocationPayload {
  name?: string;
  code?: string;
  address?: Record<string, unknown>;
  coordinates?: Record<string, unknown>;
  capacity?: number;
  businessHours?: Record<string, unknown>;
  contactEmail?: string;
  contactPhone?: string;
  isActive?: boolean;
}

// Location Services
export const locationService = {
  getAll: (includeInactive = false) =>
    api.get<LocationData[]>('/locations', {
      params: includeInactive ? { includeInactive: true } : undefined,
    }),
  getById: (id: string) => api.get('/locations/' + id),
  create: (data: LocationPayload & { name: string; code: string }) =>
    api.post<LocationData>('/locations', data),
  update: (id: string, data: LocationPayload) => api.patch<LocationData>(`/locations/${id}`, data),
  remove: (id: string) => api.delete(`/locations/${id}`),
};

// Yard Zone Management Services
export const yardZoneService = {
  getAll: (params?: { locationId?: string }) => api.get<YardZone[]>('/zones', { params }),
  getById: (id: string) => api.get<YardZone>(`/zones/${id}`),
  create: (data: {
    name: string;
    code: string;
    zoneType: string;
    locationId: string;
    capacity?: number;
    displayOrder?: number;
    config?: Record<string, unknown>;
  }) => api.post<YardZone>('/zones', data),
  update: (
    id: string,
    data: {
      name?: string;
      code?: string;
      zoneType?: string;
      capacity?: number;
      displayOrder?: number;
      config?: Record<string, unknown>;
      isActive?: boolean;
    },
  ) => api.patch<YardZone>(`/zones/${id}`, data),
  delete: (id: string) => api.delete(`/zones/${id}`),
  getPositions: (id: string) => api.get(`/zones/${id}/positions`),
};

// Yard Position Management Services
export const yardPositionService = {
  getAll: (params?: { zoneId?: string }) => api.get<YardPosition[]>('/yard/positions', { params }),
  getById: (id: string) => api.get<YardPosition>(`/yard/positions/${id}`),
  create: (data: {
    code: string;
    zoneId: string;
    positionType?: string;
    sizeCategory?: string;
    evSuitable?: boolean;
    isCovered?: boolean;
    isIndoor?: boolean;
    coordinates?: Record<string, unknown>;
  }) => api.post<YardPosition>('/yard/positions', data),
  bulkCreate: (data: {
    prefix: string;
    startNumber: number;
    count: number;
    zoneId: string;
    positionType?: string;
    sizeCategory?: string;
    evSuitable?: boolean;
    isCovered?: boolean;
    isIndoor?: boolean;
  }) => api.post<{ message: string; count: number; codes: string[] }>('/yard/positions/bulk', data),
  update: (
    id: string,
    data: {
      code?: string;
      positionType?: string;
      sizeCategory?: string;
      evSuitable?: boolean;
      isCovered?: boolean;
      isIndoor?: boolean;
      coordinates?: Record<string, unknown>;
      isActive?: boolean;
    },
  ) => api.patch<YardPosition>(`/yard/positions/${id}`, data),
  delete: (id: string) => api.delete(`/yard/positions/${id}`),
};

// Service Interval Types (recurring services, DEV-178)
export interface ServiceIntervalStep {
  id?: string;
  position?: number;
  label: string;
  serviceCode?: string | null;
  serviceCodeByFuelType?: Record<string, string> | null;
}

export interface ServiceInterval {
  id: string;
  name: string;
  description?: string | null;
  intervalDays: number;
  taskType: string;
  taskPriority: 'LOW' | 'NORMAL' | 'HIGH' | 'URGENT';
  contractId?: string | null;
  customerId?: string | null;
  contract?: { id: string; name: string; contractNumber: string } | null;
  customer?: { id: string; name: string; code: string } | null;
  isActive: boolean;
  steps: ServiceIntervalStep[];
}

export interface ServiceIntervalPayload {
  name: string;
  description?: string;
  intervalDays: number;
  taskType: string;
  taskPriority?: ServiceInterval['taskPriority'];
  contractId?: string | null;
  customerId?: string | null;
  isActive?: boolean;
  steps: Array<Pick<ServiceIntervalStep, 'label' | 'serviceCode' | 'serviceCodeByFuelType'>>;
}

export interface ServiceIntervalForecastEntry {
  serviceIntervalId: string;
  intervalName: string;
  vehicleId: string;
  vin: string;
  licensePlate: string | null;
  occurrence: number;
  stepLabel: string;
  serviceCode: string | null;
  dueAt: string;
  overdueDays: number;
}

// Service Interval Services (recurring service rules)
export const serviceIntervalService = {
  getAll: (includeInactive = false) =>
    api.get<ServiceInterval[]>('/service-intervals', { params: { includeInactive } }),
  getById: (id: string) => api.get<ServiceInterval>(`/service-intervals/${id}`),
  create: (data: ServiceIntervalPayload) => api.post<ServiceInterval>('/service-intervals', data),
  update: (id: string, data: Partial<ServiceIntervalPayload>) =>
    api.patch<ServiceInterval>(`/service-intervals/${id}`, data),
  remove: (id: string) => api.delete(`/service-intervals/${id}`),
  forecast: (days = 30) =>
    api.get<ServiceIntervalForecastEntry[]>('/service-intervals/forecast', {
      params: { days },
    }),
  run: () => api.post<{ created: number }>('/service-intervals/run'),
};

// Service Catalog — unified Service model (ServiceDefinition/Price/Instance)
export interface ServiceDef {
  id: string;
  code: string;
  name: string;
  description?: string;
  unit: string;
  behavior: string;
  category?: string;
  /** Zuständige Rolle → bestimmt, in welcher Rollen-Queue der Service landet. */
  assignToRole?: string | null;
  billable: boolean;
  defaultPriceNet?: number | null;
  defaultTaxRate?: number;
  isActive: boolean;
  prices?: {
    id: string;
    customerId: string | null;
    contractId: string | null;
    contract?: { contractNumber: string } | null;
    locationId?: string | null;
    location?: { id: string; name: string } | null;
    priceNet: number;
    taxRate: number;
  }[];
}

export const serviceCatalogService = {
  listDefinitions: (activeOnly = false) =>
    api.get<ServiceDef[]>('/services/definitions', {
      params: activeOnly ? { active: true } : undefined,
    }),
  getDefinition: (id: string) => api.get<ServiceDef>(`/services/definitions/${id}`),
  createDefinition: (data: {
    code: string;
    name: string;
    unit?: string;
    behavior?: string;
    category?: string;
    assignToRole?: string;
    billable?: boolean;
    defaultPriceNet?: number;
    description?: string;
  }) => api.post<ServiceDef>('/services/definitions', data),
  updateDefinition: (id: string, data: Partial<ServiceDef>) =>
    api.patch<ServiceDef>(`/services/definitions/${id}`, data),
  setPrice: (data: {
    serviceDefinitionId: string;
    customerId?: string;
    contractId?: string;
    locationId?: string;
    priceNet: number;
    taxRate?: number;
  }) => api.post<unknown>('/services/prices', data),
  listInstances: (params?: { vehicleId?: string; status?: string; billingState?: string }) =>
    api.get<unknown[]>('/services/instances', { params }),
  createInstance: (data: {
    serviceDefinitionId: string;
    customerId?: string;
    vehicleId?: string;
    quantity?: number;
  }) => api.post<unknown>('/services/instances', data),
  completeInstance: (
    id: string,
    data?: { quantity?: number; workedSeconds?: number; notes?: string },
  ) => api.post<unknown>(`/services/instances/${id}/complete`, data || {}),
  // Servicetypen (managed categories)
  listTypes: (activeOnly = false) =>
    api.get<ServiceType[]>('/services/types', {
      params: activeOnly ? { active: true } : undefined,
    }),
  createType: (data: { code: string; label: string; description?: string; sortOrder?: number }) =>
    api.post<ServiceType>('/services/types', data),
  updateType: (id: string, data: Partial<ServiceType>) =>
    api.patch<ServiceType>(`/services/types/${id}`, data),
  deleteType: (id: string) => api.delete(`/services/types/${id}`),
};

// Unified Service work+billing API (Arbeits-Achse). Spiegelt den alten taskService,
// zeigt aber auf die Root-/services-Endpunkte des vereinheitlichten Modells.
export interface ServiceStats {
  pending: number;
  inProgress: number;
  done: number;
  overdue: number;
  total: number;
}

export const serviceOpsService = {
  // The API's transform interceptor FLATTENS { items, meta } → { data: <array>, meta }
  // (top-level meta). Normalize it back to { data: { items, meta } } so every
  // consuming page can keep reading response.data.items / response.data.meta.
  getAll: async (params: PaginationParams & ServiceFilters) => {
    const res = (await api.get<unknown>('/services', {
      params: { ...params, page: (params.page || 1) - 1 },
    })) as { data: unknown; meta?: PaginatedResponse<ServiceInstance>['meta'] };
    const raw = res.data as ServiceInstance[] | PaginatedResponse<ServiceInstance> | undefined;
    const items = Array.isArray(raw) ? raw : raw?.items || [];
    const meta = res.meta ||
      (!Array.isArray(raw) ? raw?.meta : undefined) || {
        total: items.length,
        page: (params.page || 1) - 1,
        limit: params.limit || 20,
        totalPages: 1,
      };
    return { ...res, data: { items, meta } } as {
      data: PaginatedResponse<ServiceInstance>;
      meta?: PaginatedResponse<ServiceInstance>['meta'];
    };
  },
  getById: (id: string) => api.get<ServiceInstance>(`/services/${id}`),
  getStats: (assigneeId?: string) =>
    api.get<ServiceStats>('/services/stats', {
      params: assigneeId ? { assigneeId } : undefined,
    }),
  create: (data: {
    /** Freie Aufgabe ohne Katalog-Leistung (dann ist serviceDefinitionId leer). */
    freeTask?: boolean;
    serviceDefinitionId?: string;
    vehicleId?: string;
    customerId?: string;
    assigneeId?: string;
    title?: string;
    description?: string;
    priority?: TaskPriorityValue;
    dueDate?: string;
    quantity?: number;
    origin?: 'WORKFLOW' | 'SCHEDULE' | 'DIRECT';
  }) => api.post<ServiceInstance>('/services', data),
  update: (
    id: string,
    data: {
      title?: string;
      description?: string;
      priority?: TaskPriorityValue;
      dueDate?: string;
      assigneeId?: string;
      notes?: string;
    },
  ) => api.patch<ServiceInstance>(`/services/${id}`, data),
  assign: (id: string, assigneeId: string) => api.patch(`/services/${id}/assign`, { assigneeId }),
  start: (id: string) => api.patch(`/services/${id}/start`),
  complete: (id: string, data?: { quantity?: number; workedSeconds?: number; notes?: string }) =>
    api.post(`/services/${id}/complete`, data || {}),
  cancel: (id: string, reason?: string) => api.patch(`/services/${id}/cancel`, { reason }),
  /** Abschluss zurücknehmen — nur solange die Leistung nicht abgerechnet ist. */
  reopen: (id: string) => api.post<unknown>(`/services/instances/${id}/reopen`),
  /** Eine Leistung für viele Fahrzeuge anlegen (max. 200). */
  createBatch: (data: {
    serviceDefinitionId: string;
    vehicleIds: string[];
    assigneeId?: string;
    priority?: TaskPriorityValue;
    dueDate?: string;
    quantity?: number;
    description?: string;
  }) =>
    api.post<{
      created: number;
      failed: { vehicleId: string; reason: string }[];
      services: { vehicleId: string; serviceId: string; status: string }[];
    }>('/services/instances/batch', data),
  addNote: (id: string, note: string) => api.post(`/services/${id}/notes`, { note }),
};

type TaskPriorityValue = 'LOW' | 'NORMAL' | 'HIGH' | 'URGENT';

export interface ServiceType {
  id: string;
  code: string;
  label: string;
  description?: string;
  isActive: boolean;
  sortOrder: number;
}

// ─── System-Jobs (Job-Center) ───────────────────────────────────────────────

export interface JobRunRecord {
  id: string;
  jobName: string;
  status: 'RUNNING' | 'SUCCESS' | 'FAILED';
  trigger: 'CRON' | 'MANUAL';
  startedAt: string;
  finishedAt?: string | null;
  durationMs?: number | null;
  result?: unknown;
  error?: string | null;
  triggeredById?: string | null;
}

export interface SystemJob {
  name: string;
  description: string;
  category: string;
  enabled: boolean;
  manualTrigger: boolean;
  schedule: string | null;
  nextRun: string | null;
  isRunning: boolean;
  lastRun: JobRunRecord | null;
}

export const jobService = {
  list: () => api.get<SystemJob[]>('/jobs'),
  runs: (name: string, limit = 20) =>
    api.get<JobRunRecord[]>(`/jobs/${encodeURIComponent(name)}/runs`, { params: { limit } }),
  run: (name: string) => api.post<unknown>(`/jobs/${encodeURIComponent(name)}/run`),
  setEnabled: (name: string, enabled: boolean) =>
    api.patch<{ name: string; enabled: boolean }>(`/jobs/${encodeURIComponent(name)}`, { enabled }),
};

// ─── Standtage (Dry-Run-Vorschau) ───────────────────────────────────────────

export interface StandtagePreview {
  asOf: string;
  totals: { vehicles: number; days: number; totalNet: number };
  items: Array<{
    vehicleId: string;
    stayId?: string | null;
    vin: string;
    licensePlate: string | null;
    internalNumber: string | null;
    customerId: string | null;
    totalLossAt: string | null;
    normalDays: number;
    normalNet: number;
    totalLossDays: number;
    totalLossNet: number;
    correctionDays: number;
    correctionNet: number;
    firstDay: string | null;
    lastDay: string | null;
    count: number;
    total: number;
  }>;
}

export const standtageService = {
  preview: () => api.get<StandtagePreview>('/standtage/preview'),
};

// ─── Abrechnungsläufe (BillingRun) ──────────────────────────────────────────

export type BillingRunType = 'INVOICE' | 'STATEMENT';
export type BillingRunStatus = 'DRAFT' | 'FINALIZED' | 'SENT' | 'CANCELLED';

export interface BillingRunScope {
  customerId: string;
  contractId?: string | null;
  locationId?: string | null;
  periodStart?: string | null;
  periodEnd?: string | null;
  serviceCodes?: string[] | null;
  serviceCategories?: string[] | null;
  serviceCodeMode?: 'INCLUDE' | 'EXCLUDE' | null;
}

export interface BillingServiceCode {
  code: string;
  name: string;
  category: string | null;
  /** Lesbares Kategorie-Label (Servicetyp) statt rohem Code. */
  categoryLabel?: string | null;
  openLines: number;
}

export interface BillingRunPreview {
  lineCount: number;
  subtotalNet: number;
  taxAmount: number;
  totalGross: number;
  lines: BillableServiceRecord[];
}

export interface BillingRun {
  id: string;
  customerId: string;
  customer?: { id: string; name: string; code?: string } | null;
  contractId?: string | null;
  contract?: { id: string; contractNumber: string } | null;
  type: BillingRunType;
  status: BillingRunStatus;
  periodStart?: string | null;
  periodEnd?: string | null;
  documentNumber?: string | null;
  subtotalNet: string | number;
  taxAmount: string | number;
  totalGross: string | number;
  finalizedAt?: string | null;
  createdAt: string;
  _count?: { billingLines: number };
}

export interface ExportColumn {
  key: string;
  label: string;
  type: 'text' | 'date' | 'money' | 'number';
  required: boolean;
}

export interface ExportProfile {
  id: string;
  name: string;
  columns: string[];
  format: 'xlsx' | 'csv' | 'pdf';
  groupBy: 'location' | 'vehicle' | 'none';
  isDefault?: boolean;
}

export interface ExportRenderOptions {
  format?: 'xlsx' | 'csv' | 'pdf';
  columns?: string[];
  groupBy?: 'location' | 'vehicle' | 'none';
}

const exportParams = (o?: ExportRenderOptions) => ({
  ...(o?.format && o.format !== 'pdf' ? { format: o.format } : {}),
  ...(o?.columns?.length ? { columns: o.columns.join(',') } : {}),
  ...(o?.groupBy ? { groupBy: o.groupBy } : {}),
});

export const billingRunService = {
  preview: (scope: BillingRunScope) => api.post<BillingRunPreview>('/billing-runs/preview', scope),
  serviceCodes: (customerId: string) =>
    api.get<BillingServiceCode[]>('/billing-runs/service-codes', { params: { customerId } }),
  create: (
    data: BillingRunScope & {
      type: BillingRunType;
      notes?: string;
      invoiceDetail?: 'SERVICE' | 'VEHICLE';
    },
  ) => api.post<BillingRun>('/billing-runs', data),
  finalize: (id: string) =>
    api.post<BillingRun & { invoiceId?: string }>(`/billing-runs/${id}/finalize`),
  cancel: (id: string) => api.post<BillingRun>(`/billing-runs/${id}/cancel`),
  list: (params?: { customerId?: string; status?: string }) =>
    api.get<BillingRun[]>('/billing-runs', { params }),
  getById: (id: string) =>
    api.get<BillingRun & { billingLines: BillableServiceRecord[] }>(`/billing-runs/${id}`),
  // Spaltenkatalog + mandantenweite Export-Profile
  exportColumns: () => api.get<ExportColumn[]>('/billing-runs/export/columns'),
  exportProfiles: () => api.get<ExportProfile[]>('/billing-runs/export/profiles'),
  saveExportProfiles: (profiles: ExportProfile[]) =>
    api.put<ExportProfile[]>('/billing-runs/export/profiles', profiles),
  exportRun: async (id: string, options?: ExportRenderOptions) =>
    (options?.format === 'pdf'
      ? await api.get(`/billing-runs/${id}/document`, {
          params: exportParams(options),
          responseType: 'blob',
        })
      : await api.get(`/billing-runs/${id}/export`, {
          params: exportParams(options),
          responseType: 'blob',
        })) as unknown as Blob,
  exportOpen: async (
    params?: {
      customerId?: string;
      contractId?: string;
      locationId?: string;
      periodStart?: string;
      periodEnd?: string;
    },
    options?: ExportRenderOptions,
  ) =>
    (await api.get('/billing-runs/export/open', {
      params: { ...params, ...exportParams(options) },
      responseType: 'blob',
    })) as unknown as Blob,
  // Fixierter Leistungsnachweis (GoBD) — signierte URL.
  // Fixierter Leistungsnachweis (GoBD) — Binary durch die API gestreamt.
  attachment: async (id: string, format: 'pdf' | 'xlsx') =>
    (await api.get(`/billing-runs/${id}/attachment`, {
      params: { format },
      responseType: 'blob',
    })) as unknown as Blob,
  // Leistungsnachweis-PDF (Anhang) eines Laufs.
  document: async (id: string, options?: ExportRenderOptions) =>
    (await api.get(`/billing-runs/${id}/document`, {
      params: exportParams(options),
      responseType: 'blob',
    })) as unknown as Blob,
};
